import type { EquipmentReservation, ProcessStepConnection } from '@/api/processStepsApi'
import { reservationWindow } from '@/lib/process/reservationWindow'

// ─── What re-timing a step would actually do ─────────────────────────────────
//
// Moving the step's expected start moves every hold on it, and until now the
// operator found out what that meant only afterwards — from a one-line error if
// it was refused, or from six rows silently changing if it was not. This
// computes the before-and-after so the question can be asked before the answer
// is committed.
//
// The new times come from `reservationWindow`, the SAME module the booking path
// and the server's `deriveWindow` mirror, so a preview can never promise a time
// the re-time would not produce.
//
// Pure and dependency-free so it can be unit tested — vitest here is node-only
// and matches `src/**/*.test.ts`.

export interface RetimeRow {
  requirementId: string
  /** The unit if one is held, else the equipment type — whatever names it best. */
  label: string
  /** Where the hold sits now. */
  currentIso: string
  /** Where it would move to; null when the requirement configures no run time. */
  nextIso: string | null
  /** Already in use, so the server will refuse to move this step at all. */
  inUse: boolean
  /** The move is a no-op — the hold is already at the proposed time. */
  unchanged: boolean
}

export interface RetimePreview {
  rows: RetimeRow[]
  /** Labels of units already in use. Non-empty means the whole re-time is refused. */
  blockedBy: string[]
  /** How many holds would actually move. */
  movingCount: number
}

/**
 * Preview a whole-step re-time.
 *
 * Only LIVE HOLDS appear: re-timing moves bookings, and a requirement with
 * nothing booked has nothing to move — listing it would pad the dialog with
 * rows the action does not touch.
 */
export function retimePreview({
  requirements,
  reservations,
  newStartIso,
  labelFor,
}: {
  requirements: ProcessStepConnection['equipmentRequirements']
  reservations: EquipmentReservation[]
  /** The step's proposed new expected start, as an ISO instant. */
  newStartIso: string
  /** Resolves a held unit id to its label; falls back to the equipment type. */
  labelFor: (equipmentId: string, equipmentTypeId: string) => string
}): RetimePreview {
  const rows: RetimeRow[] = []

  for (const r of requirements) {
    const held = reservations.find(res => res.requirementId === r.id)
    if (!held) continue

    const next = reservationWindow(newStartIso, r)?.from ?? null
    rows.push({
      requirementId: r.id,
      label: labelFor(held.equipmentId, r.equipmentTypeId),
      currentIso: held.startsAt,
      nextIso: next,
      inUse: !!held.actualStart,
      // Compared as instants, not strings: the stored value and a freshly
      // derived one can differ in format while naming the same moment.
      unchanged: !!next && new Date(next).getTime() === new Date(held.startsAt).getTime(),
    })
  }

  return {
    rows,
    blockedBy: rows.filter(row => row.inUse).map(row => row.label),
    movingCount: rows.filter(row => !row.inUse && !row.unchanged && !!row.nextIso).length,
  }
}
